/**
 * Trip Atlas — the model behind the 3D trip view. Pure: no I/O, no three.js.
 *
 * It uses the drawing language of the PazNet axon drawings (the BIG-study rack
 * drawings, standard HDC-RACK-3D-001), so a trip atlas and a rack drawing read
 * as one portfolio:
 *
 *   • The projection is the standard's dimetric axon. Row axis 13.11° down-right,
 *     depth axis 47.01° down-left, vertical straight up. Foreshortening is
 *     1 : 0.931 : 0.666. Those three screen vectors are an exact orthonormal
 *     basis, so the drawing *is* an orthographic camera (AXON_BASIS). Angles
 *     never change; the view only pans and zooms.
 *   • The world axes carry trip data:
 *       row axis (X)   = time, one MONTH_W per month
 *       depth (Y)      = one lane per property, like a row of racks
 *       vertical (Z)   = days on site, like RU height
 *   • Evidence states become trip states (TRIP_STATES), with the same six-state
 *     discipline: "inferred" is never shown by colour alone (history is
 *     hatched), and red is never a state.
 *   • Runs are drawn as links between their stops. Open items are risers on the
 *     property's most recent visit.
 *
 * Every block traces to a trip row. Nothing is drawn that a query cannot find.
 */

// ─── Projection ──────────────────────────────────────────────────────────────

/** Screen-pixel vectors of the three world axes at k = 1 (y down), from the standard. */
export const AXON_SCREEN = {
  row: { x: 145.95, y: 34.0 },
  depth: { x: -68.04, y: 72.99 },
  up: { x: 0, y: -139.48 },
} as const;

/**
 * The camera basis in renderer coordinates. three.js is right-handed, so the
 * renderer puts depth on −Y (away from the viewer): renderer (x, y, z) =
 * model (X time, −Y depth, Z up). `right` and `up` are each renderer axis's
 * screen-x and screen-up components, normalised; `back` = right × up points
 * from the scene toward the camera (above, in front).
 */
export function axonBasis() {
  const r = [AXON_SCREEN.row.x, -AXON_SCREEN.depth.x, AXON_SCREEN.up.x];
  const u = [-AXON_SCREEN.row.y, AXON_SCREEN.depth.y, -AXON_SCREEN.up.y];
  const n = (v: number[]) => {
    const l = Math.hypot(...v);
    return v.map((x) => x / l) as [number, number, number];
  };
  const right = n(r);
  const up = n(u);
  const back: [number, number, number] = [
    right[1] * up[2] - right[2] * up[1],
    right[2] * up[0] - right[0] * up[2],
    right[0] * up[1] - right[1] * up[0],
  ];
  return { right, up, back, pxPerUnit: Math.hypot(...r) };
}

/** Model point (X time, Y depth toward viewer, Z up) → renderer coordinates. */
export function toRenderer(x: number, y: number, z: number): [number, number, number] {
  return [x, -y, z];
}

// ─── Trip state (the evidence discipline, applied to trips) ─────────────────

export type TripState = 'settled' | 'history' | 'ready' | 'review' | 'planned' | 'cancelled';

export interface TripStateStyle {
  label: string;
  /** What it means in the evidence language of the axon drawings. */
  evidence: 'verified' | 'inferred' | 'contested' | 'attention' | 'outline' | 'ghost';
  /** Semantic token for the fill; null = no fill (ghost). */
  fill: string | null;
  /** Semantic token for the edges. */
  edge: string;
  /** Edge dash in world units; null = solid. */
  dash: [number, number] | null;
  hatch: boolean;
  /** Fill opacity (ghost blocks read as outlines). */
  opacity: number;
}

/**
 * Order is the legend order and the evidence-bar order. Tokens only, never hex,
 * and never the notification red: red is not a state.
 */
export const TRIP_STATES: Record<TripState, TripStateStyle> = {
  settled: { label: 'Invoiced or settled', evidence: 'verified', fill: 'success', edge: 'text', dash: null, hatch: false, opacity: 1 },
  history: { label: 'Imported history — Books invoice not recorded', evidence: 'inferred', fill: 'success-soft', edge: 'text', dash: null, hatch: true, opacity: 1 },
  ready: { label: 'Completed — ready to bill', evidence: 'contested', fill: 'surface', edge: 'text', dash: [0.35, 0.35], hatch: false, opacity: 1 },
  review: { label: 'Open past its end date — needs review', evidence: 'attention', fill: 'warning-soft', edge: 'warning', dash: [0.35, 0.35], hatch: false, opacity: 1 },
  planned: { label: 'Scheduled or on site', evidence: 'outline', fill: 'surface', edge: 'text-muted', dash: null, hatch: false, opacity: 1 },
  cancelled: { label: 'Cancelled', evidence: 'ghost', fill: null, edge: 'text-muted', dash: [0.15, 0.4], hatch: false, opacity: 0 },
};

export const TRIP_STATE_ORDER: TripState[] = ['settled', 'history', 'ready', 'review', 'planned', 'cancelled'];

export interface AtlasTripInput {
  trip_id: string;
  project_number: string;
  location_id: string;
  location_name: string | null;
  customer_name: string | null;
  scope: string | null;
  trip_status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  billing_status: 'pending' | 'ready' | 'invoiced' | 'not_billable' | 'unreconciled';
  billing_basis: string | null;
  start_date: string | null;
  end_date: string | null;
  invoice_reference: string | null;
  labor_hours: number | string | null;
  is_overdue: boolean;
  lead_technician_name: string | null;
  po_number: string | null;
  /** Money from the linked Zoho CRM Deals (trip_financials); absent = no deal linked. */
  finance?: TripFinance | null;
}

/** One trip's money, from its linked Zoho CRM Deals. Zoho is master. */
export interface TripFinance {
  dealCount: number;
  /** Sum of the deals' Amount; null when no deal has an amount. */
  revenue: number | null;
  /** Labor + equipment cost; null unless every linked deal has a cost recorded. */
  cost: number | null;
  /** False while a deal has only the import snapshot (its cost fields were never read). */
  readFromZoho: boolean;
}

export function tripState(t: Pick<AtlasTripInput, 'trip_status' | 'billing_status' | 'is_overdue'>): TripState {
  if (t.trip_status === 'cancelled') return 'cancelled';
  if (t.trip_status === 'scheduled' || t.trip_status === 'in_progress') return t.is_overdue ? 'review' : 'planned';
  if (t.billing_status === 'unreconciled') return 'history';
  if (t.billing_status === 'invoiced' || t.billing_status === 'not_billable') return 'settled';
  return 'ready';
}

// ─── Layout ──────────────────────────────────────────────────────────────────

/** World units. 1 unit ≈ one screen "metre" of the axon standard. */
export const MONTH_W = 4;
export const LANE_PITCH = 6;
export const BLOCK_D = 3.2;
const MIN_W = 1.4;
const BASE_H = 1.6;
const DAY_H = 1.1;
const MAX_H = 12;
const STACK_GAP = 0.25;
const MS_DAY = 86_400_000;

export interface AtlasBlock {
  tripId: string;
  state: TripState;
  laneIndex: number;
  /** Block min corner and size, world units (X time, Y depth, Z up). */
  x: number; y: number; z: number;
  w: number; d: number; h: number;
  days: number;
  length: LengthBand;
  runId: string | null;
  runStop: number | null;
}

export interface AtlasLane {
  /** The property's location id, or the year ("2025") in a by-year atlas. */
  locationId: string;
  name: string;
  customer: string | null;
  index: number;
  /** Lane centre in Y. */
  y: number;
  tripCount: number;
  openItems: number;
}

export interface AtlasRun {
  runId: string;
  runNumber: string;
  title: string;
  /** Stop blocks in stop order. */
  tripIds: string[];
  /** Solid when every stop is completed; dashed ("partly inferred") otherwise. */
  complete: boolean;
}

export interface AtlasRiser {
  locationId: string;
  tripId: string;
  /** Riser base (top of the block) and height, world units. */
  x: number; y: number; z: number; h: number;
  index: number;
}

export interface AtlasMonth {
  x: number;
  label: string;
  isYearStart: boolean;
}

export interface TripAtlas {
  blocks: AtlasBlock[];
  lanes: AtlasLane[];
  runs: AtlasRun[];
  risers: AtlasRiser[];
  months: AtlasMonth[];
  /** First month shown (UTC, day 1) and world width. */
  origin: string;
  width: number;
  depth: number;
  /** X of `today`, when inside the range. */
  todayX: number | null;
  /** By-year atlas: the lane `today` falls in (its line spans only that lane). */
  todayLane: number | null;
  /** How lanes were assigned. */
  laneBy: 'property' | 'year';
  /** "Aug 2024 → Oct 2026": first and last month shown. */
  rangeLabel: string;
  counts: Record<TripState, number>;
  byId: Map<string, AtlasTripInput>;
  blockById: Map<string, AtlasBlock>;
}

export interface AtlasRunInput {
  run_id: string;
  run_number: string;
  title: string;
  stops: { trip_id: string; stop: number | string | null; status: string }[];
}

function parseDay(d: string): number {
  const [y, m, day] = d.slice(0, 10).split('-').map(Number);
  return Date.UTC(y, m - 1, day);
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** World X of a date, given the first month of the range. */
export function dateX(originMs: number, dayMs: number): number {
  const o = new Date(originMs);
  const d = new Date(dayMs);
  const months = (d.getUTCFullYear() - o.getUTCFullYear()) * 12 + (d.getUTCMonth() - o.getUTCMonth());
  const dim = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  return (months + (d.getUTCDate() - 1) / dim) * MONTH_W;
}

export function buildTripAtlas(input: {
  trips: AtlasTripInput[];
  runs: AtlasRunInput[];
  openItemsByLocation: Map<string, number>;
  today: string;
  /**
   * 'property' (default): one lane per property along a continuous timeline.
   * 'year': one lane per year along Jan → Dec, for one property's atlas —
   * every year reads against the same months, so seasons line up.
   */
  laneBy?: 'property' | 'year';
}): TripAtlas {
  if (input.laneBy === 'year') return buildYearAtlas(input);
  const trips = input.trips.filter((t) => t.start_date);
  const byId = new Map(input.trips.map((t) => [t.trip_id, t]));

  // Run membership, in stop order.
  const runOf = new Map<string, { runId: string; stop: number | null }>();
  for (const r of input.runs) {
    for (const s of r.stops) runOf.set(s.trip_id, { runId: r.run_id, stop: s.stop == null ? null : Number(s.stop) });
  }

  // Lanes: most-visited property first, then by name. The order is stable, so
  // two atlases of the same data line up.
  const laneMap = new Map<string, { name: string; customer: string | null; n: number }>();
  for (const t of trips) {
    const l = laneMap.get(t.location_id) ?? { name: t.location_name ?? 'Unknown property', customer: t.customer_name, n: 0 };
    l.n += 1;
    laneMap.set(t.location_id, l);
  }
  const lanes: AtlasLane[] = [...laneMap.entries()]
    .sort((a, b) => b[1].n - a[1].n || a[1].name.localeCompare(b[1].name))
    .map(([locationId, l], index) => ({
      locationId,
      name: l.name,
      customer: l.customer,
      index,
      y: index * LANE_PITCH + LANE_PITCH / 2,
      tripCount: l.n,
      openItems: input.openItemsByLocation.get(locationId) ?? 0,
    }));
  const laneIndex = new Map(lanes.map((l) => [l.locationId, l.index]));

  // Time range: whole months from the first trip (or today) to the last (or today).
  const todayMs = parseDay(input.today);
  const days = trips.flatMap((t) => [parseDay(t.start_date!), parseDay(t.end_date ?? t.start_date!)]);
  const minMs = days.length ? Math.min(...days, todayMs) : todayMs;
  const maxMs = days.length ? Math.max(...days, todayMs) : todayMs;
  const first = new Date(minMs);
  const originMs = Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1);
  const last = new Date(maxMs);
  const totalMonths = (last.getUTCFullYear() - first.getUTCFullYear()) * 12 + (last.getUTCMonth() - first.getUTCMonth()) + 1;

  const months: AtlasMonth[] = [];
  for (let i = 0; i <= totalMonths; i += 1) {
    const d = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + i, 1));
    months.push({
      x: i * MONTH_W,
      label: d.getUTCMonth() === 0 || i === 0 ? `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()}` : MONTH_NAMES[d.getUTCMonth()],
      isYearStart: d.getUTCMonth() === 0,
    });
  }

  // Blocks: per lane in date order; a block that would overlap one already placed
  // stacks on top of it (like RU on RU), so nothing hides behind anything.
  const blocks: AtlasBlock[] = [];
  const counts = Object.fromEntries(TRIP_STATE_ORDER.map((s) => [s, 0])) as Record<TripState, number>;
  const perLane = new Map<number, AtlasBlock[]>();
  const sorted = [...trips].sort((a, b) => a.start_date!.localeCompare(b.start_date!) || a.project_number.localeCompare(b.project_number));
  for (const t of sorted) {
    const li = laneIndex.get(t.location_id)!;
    const s = parseDay(t.start_date!);
    const e = Math.max(s, parseDay(t.end_date ?? t.start_date!));
    const nDays = Math.round((e - s) / MS_DAY) + 1;
    const x0 = dateX(originMs, s);
    const x1 = dateX(originMs, e + MS_DAY);
    const w = Math.max(MIN_W, x1 - x0);
    const x = w > x1 - x0 ? x0 - (w - (x1 - x0)) / 2 : x0;
    const placed = perLane.get(li) ?? [];
    const z = placed
      .filter((p) => p.x < x + w && x < p.x + p.w)
      .reduce((top, p) => Math.max(top, p.z + p.h + STACK_GAP), 0);
    const state = tripState(t);
    counts[state] += 1;
    const run = runOf.get(t.trip_id);
    const b: AtlasBlock = {
      tripId: t.trip_id,
      state,
      laneIndex: li,
      x,
      y: lanes[li].y - BLOCK_D / 2,
      z,
      w,
      d: BLOCK_D,
      h: Math.min(MAX_H, BASE_H + DAY_H * (nDays - 1)),
      days: nDays,
      length: lengthBand(nDays),
      runId: run?.runId ?? null,
      runStop: run?.stop ?? null,
    };
    placed.push(b);
    perLane.set(li, placed);
    blocks.push(b);
  }
  const blockById = new Map(blocks.map((b) => [b.tripId, b]));

  const runs: AtlasRun[] = input.runs
    .map((r) => ({
      runId: r.run_id,
      runNumber: r.run_number,
      title: r.title,
      tripIds: [...r.stops]
        .sort((a, b) => Number(a.stop ?? 0) - Number(b.stop ?? 0))
        .map((s) => s.trip_id)
        .filter((id) => blockById.has(id)),
      complete: r.stops.every((s) => s.status === 'completed' || s.status === 'cancelled'),
    }))
    .filter((r) => r.tripIds.length > 0);

  // Open items rise from the property's most recent visit.
  const risers: AtlasRiser[] = [];
  for (const lane of lanes) {
    if (lane.openItems <= 0) continue;
    const latest = blocks
      .filter((b) => b.laneIndex === lane.index && b.state !== 'cancelled')
      .sort((a, b) => b.x - a.x)[0];
    if (!latest) continue;
    const shown = Math.min(lane.openItems, 5);
    for (let j = 0; j < shown; j += 1) {
      risers.push({
        locationId: lane.locationId,
        tripId: latest.tripId,
        x: latest.x + Math.min(latest.w - 0.2, 0.3 + 0.45 * j),
        y: latest.y + latest.d * 0.5,
        z: latest.z + latest.h,
        h: 1.4 + 0.35 * j,
        index: j,
      });
    }
  }

  const width = totalMonths * MONTH_W;
  const tx = dateX(originMs, todayMs);
  return {
    blocks,
    lanes,
    runs,
    risers,
    months,
    origin: new Date(originMs).toISOString().slice(0, 10),
    width,
    depth: lanes.length * LANE_PITCH,
    todayX: tx >= 0 && tx <= width ? tx : null,
    todayLane: null,
    laneBy: 'property',
    rangeLabel: `${MONTH_NAMES[first.getUTCMonth()]} ${first.getUTCFullYear()} → ${MONTH_NAMES[last.getUTCMonth()]} ${last.getUTCFullYear()}`,
    counts,
    byId,
    blockById,
  };
}

/** Proportions for the evidence bar, in state order; zero-count states omitted. */
export function evidenceBar(counts: Record<TripState, number>): { state: TripState; count: number; share: number }[] {
  const total = TRIP_STATE_ORDER.reduce((n, s) => n + counts[s], 0);
  return TRIP_STATE_ORDER.filter((s) => counts[s] > 0).map((s) => ({ state: s, count: counts[s], share: total ? counts[s] / total : 0 }));
}

export function tripDays(t: Pick<AtlasTripInput, 'start_date' | 'end_date'>): number {
  if (!t.start_date) return 0;
  return Math.round((parseDay(t.end_date ?? t.start_date) - parseDay(t.start_date)) / MS_DAY) + 1;
}

// ─── Browsing ────────────────────────────────────────────────────────────────

/**
 * The previous or next visit to the same property, in date order (← / → in
 * the atlas). Cancelled visits are skipped; returns null at either end.
 */
export function adjacentVisit(atlas: TripAtlas, tripId: string, dir: -1 | 1): string | null {
  const b = atlas.blockById.get(tripId);
  if (!b) return null;
  const lane = atlas.blocks
    .filter((x) => x.laneIndex === b.laneIndex && (x.state !== 'cancelled' || x.tripId === tripId))
    .sort((p, q) => p.x - q.x || p.z - q.z);
  const i = lane.findIndex((x) => x.tripId === tripId);
  return lane[i + dir]?.tripId ?? null;
}

/** Step through an ordered list of trip ids (↑ / ↓ through results), clamped at the ends. */
export function stepInList(ids: string[], current: string | null, dir: -1 | 1): string | null {
  if (!ids.length) return null;
  const i = current ? ids.indexOf(current) : -1;
  if (i === -1) return dir === 1 ? ids[0] : ids[ids.length - 1];
  return ids[Math.max(0, Math.min(ids.length - 1, i + dir))];
}

/** Calendar years the atlas covers, oldest first (the year chips). */
export function atlasYears(atlas: TripAtlas): number[] {
  const years = new Set<number>();
  for (const t of atlas.byId.values()) {
    if (!t.start_date) continue;
    const y0 = Number(t.start_date.slice(0, 4));
    const y1 = Number((t.end_date ?? t.start_date).slice(0, 4));
    for (let y = y0; y <= y1; y += 1) years.add(y);
  }
  return [...years].sort((a, b) => a - b);
}

// ─── Trip length ─────────────────────────────────────────────────────────────

/** Trips this long or longer are called out everywhere (badge, colour, filter). */
export const LONG_TRIP_DAYS = 4;

export type LengthBand = 'day' | 'short' | 'long';

export function lengthBand(days: number): LengthBand {
  return days >= LONG_TRIP_DAYS ? 'long' : days >= 2 ? 'short' : 'day';
}

// ─── Colour modes ────────────────────────────────────────────────────────────

/**
 * What a block's colour means. State is the evidence discipline; the others
 * answer one question each. Hatching (imported history) shows in every mode,
 * so evidence is never carried by colour alone. Tokens only; red is never used.
 */
export type ColorBy = 'state' | 'length' | 'revenue' | 'margin';

export const COLOR_BY_LABEL: Record<ColorBy, string> = {
  state: 'State',
  length: 'Length',
  revenue: 'Revenue',
  margin: 'Margin',
};

export interface ColorBand {
  key: string;
  label: string;
  /** Semantic token for the fill. */
  fill: string;
}

export const COLOR_BANDS: Record<Exclude<ColorBy, 'state'>, ColorBand[]> = {
  length: [
    { key: 'day', label: '1 day', fill: 'surface-3' },
    { key: 'short', label: '2–3 days', fill: 'info-soft' },
    { key: 'long', label: `${LONG_TRIP_DAYS}+ days`, fill: 'info' },
  ],
  revenue: [
    { key: 'none', label: 'No deal amount', fill: 'surface' },
    { key: 'zero', label: '$0', fill: 'surface-3' },
    { key: 'low', label: 'Under $2,500', fill: 'success-soft' },
    { key: 'mid', label: '$2,500 – $10,000', fill: 'success' },
    { key: 'high', label: '$10,000 and up', fill: 'accent-gold' },
  ],
  margin: [
    { key: 'unknown', label: 'Cost not recorded in Zoho', fill: 'surface' },
    { key: 'loss', label: 'Loss', fill: 'warning' },
    { key: 'thin', label: 'Under 25%', fill: 'warning-soft' },
    { key: 'fair', label: '25% – 50%', fill: 'success-soft' },
    { key: 'strong', label: '50% and up', fill: 'success' },
  ],
};

/** Margin = (revenue − cost) / revenue; null unless both are recorded and revenue > 0. */
export function tripMargin(f: TripFinance | null | undefined): number | null {
  if (!f || f.revenue === null || f.cost === null || f.revenue <= 0) return null;
  return (f.revenue - f.cost) / f.revenue;
}

/** The band a trip falls in for a colour mode (state mode returns the state). */
export function colorBandKey(mode: ColorBy, block: AtlasBlock, trip: AtlasTripInput | undefined): string {
  if (mode === 'state') return block.state;
  if (mode === 'length') return block.length;
  const f = trip?.finance;
  if (mode === 'revenue') {
    if (!f || f.revenue === null) return 'none';
    if (f.revenue === 0) return 'zero';
    return f.revenue < 2500 ? 'low' : f.revenue < 10000 ? 'mid' : 'high';
  }
  if (f && f.cost !== null && f.revenue !== null && f.revenue - f.cost < 0) return 'loss';
  const m = tripMargin(f);
  if (m === null) return 'unknown';
  return m < 0.25 ? 'thin' : m < 0.5 ? 'fair' : 'strong';
}

/** Fill token for a block in a colour mode; null = no fill (a cancelled ghost). */
export function blockFill(mode: ColorBy, block: AtlasBlock, trip: AtlasTripInput | undefined): string | null {
  if (block.state === 'cancelled') return null;
  if (mode === 'state') return TRIP_STATES[block.state].fill;
  const key = colorBandKey(mode, block, trip);
  return COLOR_BANDS[mode].find((b) => b.key === key)!.fill;
}

/** Legend rows for a non-state colour mode, with how many visible trips fall in each. */
export function colorLegend(mode: Exclude<ColorBy, 'state'>, atlas: TripAtlas, visible: Set<string> | null) {
  const counts = new Map<string, number>();
  for (const b of atlas.blocks) {
    if (b.state === 'cancelled' || (visible && !visible.has(b.tripId))) continue;
    const k = colorBandKey(mode, b, atlas.byId.get(b.tripId));
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return COLOR_BANDS[mode].map((band) => ({ ...band, count: counts.get(band.key) ?? 0 }));
}

// ─── Periods: all trips, one year, one quarter ───────────────────────────────

export type AtlasPeriod = { kind: 'all' } | { kind: 'year'; year: number } | { kind: 'quarter'; year: number; quarter: 1 | 2 | 3 | 4 };

/**
 * A trip belongs to the period its visit starts in, for both what is shown and
 * what is counted, so a trip is never counted in two quarters.
 */
export function tripInPeriod(t: Pick<AtlasTripInput, 'start_date'>, p: AtlasPeriod): boolean {
  if (p.kind === 'all') return true;
  if (!t.start_date) return false;
  const y = Number(t.start_date.slice(0, 4));
  if (y !== p.year) return false;
  if (p.kind === 'year') return true;
  const m = Number(t.start_date.slice(5, 7));
  return Math.ceil(m / 3) === p.quarter;
}

export function periodLabel(p: AtlasPeriod): string {
  return p.kind === 'all' ? 'All trips' : p.kind === 'year' ? String(p.year) : `Q${p.quarter} ${p.year}`;
}

/** URL form: "all", "2025", "2025-q3". */
export function formatPeriod(p: AtlasPeriod): string {
  return p.kind === 'all' ? 'all' : p.kind === 'year' ? String(p.year) : `${p.year}-q${p.quarter}`;
}

export function parsePeriod(raw: string | null | undefined): AtlasPeriod {
  const v = (raw ?? '').trim().toLowerCase();
  const q = /^(\d{4})-q([1-4])$/.exec(v);
  if (q) return { kind: 'quarter', year: Number(q[1]), quarter: Number(q[2]) as 1 | 2 | 3 | 4 };
  if (/^\d{4}$/.test(v)) return { kind: 'year', year: Number(v) };
  return { kind: 'all' };
}

// ─── Money and activity summaries ────────────────────────────────────────────

export interface TripSummary {
  trips: number;
  /** Days on site across the trips. */
  days: number;
  longTrips: number;
  /** Trips with at least one linked Zoho deal. */
  withDeal: number;
  /** Sum of deal amounts, over trips that have one. */
  revenue: number;
  pricedTrips: number;
  /** Trips whose cost is recorded in Zoho, and their revenue and cost. */
  costedTrips: number;
  costedRevenue: number;
  cost: number;
  /** costedRevenue − cost; null when no trip has a cost recorded. */
  profit: number | null;
  /** profit / costedRevenue; null when unknown or costed revenue is 0. */
  margin: number | null;
}

/** Cancelled trips are excluded: they were not visits and carry no money. */
export function summarizeTrips(trips: AtlasTripInput[]): TripSummary {
  const s: TripSummary = { trips: 0, days: 0, longTrips: 0, withDeal: 0, revenue: 0, pricedTrips: 0, costedTrips: 0, costedRevenue: 0, cost: 0, profit: null, margin: null };
  for (const t of trips) {
    if (t.trip_status === 'cancelled' || !t.start_date) continue;
    const d = tripDays(t);
    s.trips += 1;
    s.days += d;
    if (d >= LONG_TRIP_DAYS) s.longTrips += 1;
    const f = t.finance;
    if (!f || f.dealCount === 0) continue;
    s.withDeal += 1;
    if (f.revenue !== null) {
      s.revenue += f.revenue;
      s.pricedTrips += 1;
    }
    if (f.cost !== null) {
      s.costedTrips += 1;
      s.costedRevenue += f.revenue ?? 0;
      s.cost += f.cost;
    }
  }
  if (s.costedTrips > 0) {
    s.profit = s.costedRevenue - s.cost;
    s.margin = s.costedRevenue > 0 ? s.profit / s.costedRevenue : null;
  }
  return s;
}

/** Four quarters of one year (by visit start). */
export function quarterSummaries(trips: AtlasTripInput[], year: number): { quarter: 1 | 2 | 3 | 4; summary: TripSummary }[] {
  return ([1, 2, 3, 4] as const).map((quarter) => ({
    quarter,
    summary: summarizeTrips(trips.filter((t) => tripInPeriod(t, { kind: 'quarter', year, quarter }))),
  }));
}

/** One summary per calendar year that has a trip, newest first. */
export function yearSummaries(trips: AtlasTripInput[]): { year: number; summary: TripSummary }[] {
  const years = [...new Set(trips.filter((t) => t.start_date).map((t) => Number(t.start_date!.slice(0, 4))))].sort((a, b) => b - a);
  return years.map((year) => ({ year, summary: summarizeTrips(trips.filter((t) => tripInPeriod(t, { kind: 'year', year }))) }));
}

// ─── One property's atlas: a lane per year, Jan → Dec ────────────────────────

function buildYearAtlas(input: Parameters<typeof buildTripAtlas>[0]): TripAtlas {
  const trips = input.trips.filter((t) => t.start_date);
  const byId = new Map(input.trips.map((t) => [t.trip_id, t]));
  const runOf = new Map<string, { runId: string; stop: number | null }>();
  for (const r of input.runs) for (const s of r.stops) runOf.set(s.trip_id, { runId: r.run_id, stop: s.stop == null ? null : Number(s.stop) });

  const todayMs = parseDay(input.today);
  const todayYear = new Date(todayMs).getUTCFullYear();
  // Oldest year at the back, the newest in front (nearest the viewer).
  const yearsSet = new Set(trips.map((t) => Number(t.start_date!.slice(0, 4))));
  if (yearsSet.size === 0) yearsSet.add(todayYear);
  const years = [...yearsSet].sort((a, b) => a - b);
  const lanes: AtlasLane[] = years.map((y, index) => ({
    locationId: String(y),
    name: String(y),
    customer: null,
    index,
    y: index * LANE_PITCH + LANE_PITCH / 2,
    tripCount: trips.filter((t) => t.start_date!.startsWith(String(y))).length,
    openItems: 0,
  }));
  const laneOf = new Map(lanes.map((l) => [Number(l.name), l.index]));

  const months: AtlasMonth[] = Array.from({ length: 13 }, (_, i) => ({
    x: i * MONTH_W,
    label: i < 12 ? MONTH_NAMES[i] : '',
    isYearStart: i === 0 || i === 12,
  }));

  const blocks: AtlasBlock[] = [];
  const counts = Object.fromEntries(TRIP_STATE_ORDER.map((s) => [s, 0])) as Record<TripState, number>;
  const perLane = new Map<number, AtlasBlock[]>();
  const sorted = [...trips].sort((a, b) => a.start_date!.localeCompare(b.start_date!) || a.project_number.localeCompare(b.project_number));
  for (const t of sorted) {
    const year = Number(t.start_date!.slice(0, 4));
    const li = laneOf.get(year)!;
    const originMs = Date.UTC(year, 0, 1);
    const s = parseDay(t.start_date!);
    const e = Math.max(s, parseDay(t.end_date ?? t.start_date!));
    const nDays = Math.round((e - s) / MS_DAY) + 1;
    const x0 = dateX(originMs, s);
    const x1 = dateX(originMs, e + MS_DAY);
    const w = Math.max(MIN_W, x1 - x0);
    const x = w > x1 - x0 ? x0 - (w - (x1 - x0)) / 2 : x0;
    const placed = perLane.get(li) ?? [];
    const z = placed.filter((p) => p.x < x + w && x < p.x + p.w).reduce((top, p) => Math.max(top, p.z + p.h + STACK_GAP), 0);
    const state = tripState(t);
    counts[state] += 1;
    const run = runOf.get(t.trip_id);
    const b: AtlasBlock = {
      tripId: t.trip_id, state, laneIndex: li,
      x, y: lanes[li].y - BLOCK_D / 2, z, w, d: BLOCK_D,
      h: Math.min(MAX_H, BASE_H + DAY_H * (nDays - 1)),
      days: nDays, length: lengthBand(nDays),
      runId: run?.runId ?? null, runStop: run?.stop ?? null,
    };
    placed.push(b);
    perLane.set(li, placed);
    blocks.push(b);
  }
  const blockById = new Map(blocks.map((b) => [b.tripId, b]));

  const runs: AtlasRun[] = input.runs
    .map((r) => ({
      runId: r.run_id, runNumber: r.run_number, title: r.title,
      tripIds: [...r.stops].sort((a, b) => Number(a.stop ?? 0) - Number(b.stop ?? 0)).map((s) => s.trip_id).filter((id) => blockById.has(id)),
      complete: r.stops.every((s) => s.status === 'completed' || s.status === 'cancelled'),
    }))
    .filter((r) => r.tripIds.length > 0);

  // Open items rise from the most recent visit, whatever year it was.
  const openItems = [...input.openItemsByLocation.values()].reduce((n, v) => n + v, 0);
  const risers: AtlasRiser[] = [];
  const latest = [...blocks].filter((b) => b.state !== 'cancelled').sort((a, b) => b.laneIndex - a.laneIndex || b.x - a.x)[0];
  if (latest && openItems > 0) {
    for (let j = 0; j < Math.min(openItems, 5); j += 1) {
      risers.push({
        locationId: byId.get(latest.tripId)!.location_id, tripId: latest.tripId,
        x: latest.x + Math.min(latest.w - 0.2, 0.3 + 0.45 * j), y: latest.y + latest.d * 0.5,
        z: latest.z + latest.h, h: 1.4 + 0.35 * j, index: j,
      });
    }
  }

  const todayLane = laneOf.get(todayYear) ?? null;
  return {
    blocks, lanes, runs, risers, months,
    origin: `${years[0]}-01-01`,
    width: 12 * MONTH_W,
    depth: lanes.length * LANE_PITCH,
    todayX: todayLane === null ? null : dateX(Date.UTC(todayYear, 0, 1), todayMs),
    todayLane,
    laneBy: 'year',
    rangeLabel: years.length > 1 ? `${years[0]} → ${years[years.length - 1]}` : String(years[0]),
    counts, byId, blockById,
  };
}
